"use client";

import { cn } from "../../utils";
import { Image, type ImageProps } from "../image";

// Round flags, one per ISO 3166-1 alpha-2 code ("fr.svg"), from `circle-flags` (MIT) uploaded to the public asset bucket.
// The bucket's `/flags/` holds the API's 3:2 flags, which neither `object-cover` nor `object-fill` fits in a round slot.
const FLAG_BASE_URL = "https://pharaday-public.s3.eu-west-3.amazonaws.com/flags-round";

const REGION_NAMES = new Intl.DisplayNames(["en"], { type: "region" });

/** "FR" → "France". An unknown or malformed code resolves to itself rather than throwing. */
export const countryNameOf = (code: string): string => {
  try {
    return REGION_NAMES.of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
};

// Any image extension: the API's country icons moved from `.svg` to `.png` (backend migration eng-2799), and flagcdn-style
// URLs (".../w320/sg.png") read the same way.
const FLAG_URL_CODE_PATTERN = /\/([\w-]+)\.(?:svg|png|jpe?g|webp|gif)(?:[?#].*)?$/i;

/**
 * ".../flags/fr.png" → "fr", for data that carries a country's flag as the URL of its image rather than its code.
 * Yields nothing for a URL it can't read, so render the flag only once there is a code:
 * `{code && <CountryFlag code={code} />}`.
 */
export const countryCodeOfFlagUrl = (url: string | null | undefined): string | undefined =>
  url ? FLAG_URL_CODE_PATTERN.exec(url)?.[1]?.toLowerCase() : undefined;

export type CountryFlagProps = Omit<ImageProps, "src"> & {
  /** ISO 3166-1 alpha-2 ("FR", "BE"). Case-insensitive. */
  code: string;
};

/**
 * A country's flag, from its ISO code alone, drawn round: an `Image`, pulsing while it loads. Its `alt` defaults to the
 * country's name. Size it like an icon (`size-4`); it defaults to 20px. For a tooltip, wrap it in
 * `<BasicTooltip asChild>`: it takes the trigger's ref and props.
 */
const CountryFlag = ({ code, alt, className, ...props }: CountryFlagProps) => (
  <Image
    {...props}
    src={`${FLAG_BASE_URL}/${code.toLowerCase()}.svg`}
    alt={alt ?? countryNameOf(code)}
    className={cn("block size-5 shrink-0 rounded-full", className)}
  />
);

CountryFlag.displayName = "CountryFlag";
export default CountryFlag;
